/**
 * wlChips.js — ЕДИНЫЙ каталог чипов окна/уровня. БЕЗ ЕДИНОГО ИМПОРТА —
 * сознательно (приём printUtil/wlManual): чистые данные и чистые функции,
 * проверяемые без DOM и без заглушек.
 *
 * ПОВОД [11.1.17], аудит §2.2: гридов чипов было ДВА рукописных (правая
 * панель и мобильный ящик), и они уже разъехались — в мобильном пропали
 * Средостение, Височная кость, Таз, PET, US, рентген-Кости/Мягк. и, больнее
 * всего, «Ангио (кино)» с авто-окном: именно на телефоне, где ангио-кино и
 * смотрят. Третьим источником тех же понятий жила _QUICK_WL в
 * viewportKeyboard со СВОИМИ числами (Кости там были 2000/300 против 2500/480
 * на чипе — дрейф №4). Теперь источник один: оба грида РЕНДЕРЯТСЯ отсюда
 * (sidebarBindings.построитьЧипыОкон), быстрые клавиши берут числа отсюда же,
 * а шкала ручных ползунков выводится из максимумов каталога (§2.1: чип
 * PET 30000/15000 не помещался в зашитые max 8000/4000 — при том что
 * wlmanual_test прямо пиннит «у ПЭТ — тысячи»).
 *
 * МРТ [11.1.17], аудит §2.3: интенсивности МР произвольны по аппарату —
 * «одно число на все аппараты не подберёшь» (довод 10.3.2, по которому XA
 * получил перцентильное авто-окно). Четвёрка T1/T2/FLAIR/DWI переведена на
 * тот же механизм: auto:'percentile', а прежние числа оставлены ЗАТРАВКОЙ —
 * применяются с оговоркой, только пока кадр не готов.
 *
 * Реестр windowPresets («Quantum», BUILTIN_PRESETS) НЕ дублируется и не
 * трогается: он — движок сценариев и авторекомендаций по тегам; каталог ниже
 * — источник КНОПОК панели. Сверять их держит пин verify_11_1_17.
 */

/** @typedef {{id:string,label:string,ww?:number,wc?:number,auto?:'percentile',title?:string,мод?:string}} ЧипОкна */

const _ГРУППЫ = [
  {
    // [12.0.48] Заголовки по-русски и одним языком (были «CT», «MRI» рядом с «Рентген»);
    // разметка (index.html) пишет те же слова — сверяет tests/wlpresets_test.mjs.
    ключ: 'ct', заголовок: 'КТ', мод: 'CT', чипы: [
      { id: 'ct-abdomen',    label: 'Живот',          ww: 400,   wc: 40 },
      { id: 'ct-lung',       label: 'Лёгкие',         ww: 1500,  wc: -600 },
      { id: 'ct-bone',       label: 'Кости',          ww: 2500,  wc: 480 },
      { id: 'ct-brain',      label: 'Мозг',           ww: 80,    wc: 40 },
      { id: 'ct-stroke',     label: 'Инсульт',        ww: 40,    wc: 40 },
      { id: 'ct-subdural',   label: 'Субдурал',       ww: 200,   wc: 80 },
      { id: 'ct-soft',       label: 'Мягк. ткани',    ww: 350,   wc: 50 },
      { id: 'ct-liver',      label: 'Печень',         ww: 150,   wc: 30 },
      { id: 'ct-mediastinum',label: 'Средостение',    ww: 350,   wc: 50 },
      { id: 'ct-pe',         label: 'ТЭЛА',           ww: 700,   wc: 100 },
      { id: 'ct-temporal',   label: 'Височная кость', ww: 4000,  wc: 700 },
      { id: 'ct-spine',      label: 'Позвоноч.',      ww: 300,   wc: 40 },
      { id: 'ct-angio',      label: 'Ангио',          ww: 600,   wc: 170 },
      { id: 'ct-chest',      label: 'Грудная кл.',    ww: 400,   wc: 40 },
      { id: 'ct-pelvis',     label: 'Таз',            ww: 400,   wc: 40 },
    ],
  },
  {
    ключ: 'mri', заголовок: 'МРТ', мод: 'MR', чипы: [
      // auto: окно считается по 1–99 перцентилю кадра; числа — лишь затравка
      // на случай «кадр ещё не готов», с оговоркой в тосте.
      { id: 'mr-t1',    label: 'T1',    auto: 'percentile', ww: 500,  wc: 250,
        title: 'Окно по самому кадру (1–99 перцентиль): интенсивности МР произвольны по аппарату. Числа 500/250 — лишь затравка, пока кадр не готов' },
      { id: 'mr-t2',    label: 'T2',    auto: 'percentile', ww: 600,  wc: 300,
        title: 'Окно по самому кадру (1–99 перцентиль). Затравка 600/300 — только пока кадр не готов' },
      { id: 'mr-flair', label: 'FLAIR', auto: 'percentile', ww: 1200, wc: 600,
        title: 'Окно по самому кадру (1–99 перцентиль). Затравка 1200/600 — только пока кадр не готов' },
      { id: 'mr-dwi',   label: 'DWI',   auto: 'percentile', ww: 1000, wc: 500,
        title: 'Окно по самому кадру (1–99 перцентиль). Затравка 1000/500 — только пока кадр не готов' },
    ],
  },
  {
    // [12.0.55] мод — для какой модальности чип (data-mod, ui/modalityPanel.js):
    // у группы — общий набор, у чипа — свой, если в группе их несколько.
    ключ: 'xr', заголовок: 'Рентген и ангиография', мод: 'XR XA', чипы: [
      { id: 'xr-chest', label: 'Грудная кл.', ww: 2048, wc: 1024, мод: 'XR' },
      { id: 'xr-bone',  label: 'Кости',       ww: 2500, wc: 1000, мод: 'XR' },
      { id: 'xr-soft',  label: 'Мягк. ткани', ww: 400,  wc: 200,  мод: 'XR' },
      // [10.3.2] У съёмки с С-дуги в тегах нет WindowCenter/Width, диапазон
      // зависит от аппарата и дозы — окно считается по кадру. Затравки нет
      // нарочно: типовых чисел у XA не существует.
      { id: 'xa-auto',  label: 'Ангио (кино)', auto: 'percentile', мод: 'XA',
        title: 'Окно по самому кадру: 1–99 перцентиль' },
    ],
  },
  {
    ключ: 'other', заголовок: 'Маммография, NM, ПЭТ, УЗИ', мод: 'MG NM PT US', чипы: [
      // [12.0.49] Маммография — по кадру. Жёстких 2048/1024 хватало только
      // 12-битным снимкам: аппараты пишут 10, 12 и 14 бит, и на 14-битном
      // (0…16383) окно 0…2048 давало сплошь белую железу. Затравки нет
      // нарочно, как у XA: общих чисел у маммографии не существует.
      { id: 'mg-mammo', label: 'Маммо', auto: 'percentile', мод: 'MG',
        title: 'Окно по самому кадру (1–99 перцентиль): разрядность маммограмм разная — 10, 12 или 14 бит' },
      // [12.0.48] NM — по кадру, как МРТ и XA: счёт импульсов зависит от
      // аппарата, дозы и времени набора; 256/128 — лишь затравка.
      { id: 'nm',       label: 'NM',    auto: 'percentile', ww: 256, wc: 128, мод: 'NM',
        title: 'Окно по самому кадру (1–99 перцентиль): счёт импульсов NM зависит от аппарата и времени набора. Затравка 256/128 — только пока кадр не готов' },
      // [12.0.49] ПЭТ — по кадру. Числа ПЭТ — активность в Бк/мл: зависят от
      // введённой дозы, веса и времени от введения, а при пересчёте в SUV
      // становятся единицами. 30000/15000 подходили одному случаю из многих.
      // Затравки нет нарочно; нижний край по кадру — фон, то есть около нуля,
      // как и принято показывать ПЭТ.
      { id: 'pt-pet',   label: 'PET',   auto: 'percentile', мод: 'PT',
        title: 'Окно по самому кадру (1–99 перцентиль): активность ПЭТ зависит от дозы, веса и времени после введения' },
      { id: 'us',       label: 'US',    ww: 255,   wc: 128, мод: 'US' },
    ],
  },
];

export const ГРУППЫ_ЧИПОВ = _ГРУППЫ;

/** Быстрый доступ по id — для _QUICK_WL и проверок. */
export const ЧИПЫ_ПО_ID = Object.freeze(Object.fromEntries(
  _ГРУППЫ.flatMap((г) => г.чипы).map((ч) => [ч.id, ч])));

const _экр = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * [12.0.48] Подсказка чипа: числа окна и синонимы. Врач видел только слово
 * («Живот») и не знал, что под ним 400/40, а что «Грудная кл.» и «Таз» дают
 * ровно то же окно. У авто-чипов — их собственное объяснение.
 * @param {ЧипОкна} ч
 */
export function подсказкаЧипа(ч) {
  if (ч.title) return ч.title;
  if (!Number.isFinite(ч.ww) || !Number.isFinite(ч.wc)) return '';
  const группа = _ГРУППЫ.find((г) => г.чипы.includes(ч));
  const те_же = (группа?.чипы || []).filter((д) => д !== ч && !д.auto && д.ww === ч.ww && д.wc === ч.wc).map((д) => `«${д.label}»`);
  return `Окно W ${ч.ww}, уровень L ${ч.wc}` + (те_же.length ? ` — то же окно, что ${те_же.join(', ')}` : '');
}

/**
 * HTML одного чипа. aria-pressed ставится сразу (аудит §2.5) — переключает
 * его обработчик в sidebarBindings.
 * @param {ЧипОкна} ч
 */
export function chipHtml(ч) {
  const а = [`class="preset-chip"`, `data-window-preset="${_экр(ч.id)}"`, `aria-pressed="false"`];
  if (ч.auto) а.push(`data-wl-auto="1"`);
  if (ч.ww != null) а.push(`data-ww="${ч.ww}"`);
  if (ч.wc != null) а.push(`data-wc="${ч.wc}"`);
  // [12.0.55] Модальность чипа: своя или группы (chipHtml зовут по группам).
  const мод = ч.мод || _ГРУППЫ.find((г) => г.чипы.includes(ч))?.мод;
  if (мод) а.push(`data-mod="${_экр(мод)}"`);
  const т = подсказкаЧипа(ч);
  if (т) а.push(`title="${_экр(т)}"`);
  return `<button ${а.join(' ')}>${_экр(ч.label)}</button>`;
}

/**
 * Пределы шкалы ручных ползунков — из максимумов каталога, а не из чисел,
 * зашитых в разметку (аудит §2.1). Затравки МРТ тоже в счёт: их применяют.
 * Нижняя граница уровня не поднимается выше −2000 (нужна КТ-лёгким).
 */
/** [12.0.49] Пол шкалы ручного W/L под ПЭТ в Бк/мл (прежние числа чипа PET). */
export const ШКАЛА_ПЭТ = Object.freeze({ ww: 30000, wc: 15000 });

export function пределыШкалы() {
  const все = _ГРУППЫ.flatMap((г) => г.чипы);
  const ww = все.map((ч) => ч.ww).filter(Number.isFinite);
  const wc = все.map((ч) => ч.wc).filter(Number.isFinite);
  return {
    // [12.0.49] У чипа PET больше нет чисел, но шкала по-прежнему должна
    // вмещать ПЭТ в Бк/мл (тысячи и десятки тысяч) ещё до того, как окно по
    // кадру растянет её (подстроитьШкалу) — пол ПЭТ задан здесь явно.
    wwMax: Math.max(8000, ШКАЛА_ПЭТ.ww, ...ww),
    wcMax: Math.max(4000, ШКАЛА_ПЭТ.wc, ...wc),
    wcMin: Math.min(-2000, ...wc),
  };
}

export default { ГРУППЫ_ЧИПОВ, ЧИПЫ_ПО_ID, chipHtml, подсказкаЧипа, пределыШкалы };
